import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  Firestore,
  collection,
  doc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  where,
  collectionData,
  collectionGroup,
  runTransaction,
  increment,
  docData
} from '@angular/fire/firestore';

import {
  UpgradeSession,
  SessionCreateData,
  UpgradeEvent,
  EventCreateData,
  DateRange,
  SessionStatus,
  DeviceSnapshot
} from '../models';

const SGP_UPGRADE_COLLECTION = 'SGP_UPGRADE';
const LOGS_SUBCOLLECTION = 'logs';
const EVENTS_SUBCOLLECTION = 'events';

@Injectable({
  providedIn: 'root'
})
export class UpgradeLoggerRepository {

  constructor(private readonly firestore: Firestore) {}

  async createSession(deviceSerial: string, sessionData: SessionCreateData): Promise<string> {
    const sessionId = `${sessionData.operatorId}_${Date.now()}`;
    const sessionRef = doc(
      this.firestore,
      `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}/${sessionId}`
    );

    const session: UpgradeSession = {
      ...sessionData,
      sessionId,
      status: 'ongoing',
      startTime: Date.now(),
      lastUpdated: Date.now(),
      epochStart: Date.now()
    };

    await runTransaction(this.firestore, async (transaction) => {
      // Create session document
      transaction.set(sessionRef, session);

      // Update device metadata
      const deviceRef = doc(this.firestore, `${SGP_UPGRADE_COLLECTION}/${deviceSerial}`);
      transaction.set(deviceRef, {
        deviceModel: sessionData.deviceModel,
        lastUpgrade: Date.now(),
        totalUpgrades: increment(1)
      }, { merge: true });
    });

    return sessionId;
  }

  async logEvent(deviceSerial: string, sessionId: string, eventData: EventCreateData): Promise<void> {
    const event: UpgradeEvent = {
      ...eventData,
      timestamp: Date.now()
    };

    const eventRef = collection(
      this.firestore,
      `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}/${sessionId}/${EVENTS_SUBCOLLECTION}`
    );

    await runTransaction(this.firestore, async (transaction) => {
      // Add event
      const newEventRef = doc(eventRef);
      transaction.set(newEventRef, event);

      // Update session lastUpdated
      const sessionRef = doc(
        this.firestore,
        `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}/${sessionId}`
      );
      transaction.update(sessionRef, { lastUpdated: Date.now() });
    });
  }

  async updateSessionStatus(deviceSerial: string, sessionId: string, status: SessionStatus): Promise<void> {
    const sessionRef = doc(
      this.firestore,
      `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}/${sessionId}`
    );

    await updateDoc(sessionRef, {
      status,
      lastUpdated: Date.now()
    });
  }

  async updateSessionEndTime(deviceSerial: string, sessionId: string, endTime: number): Promise<void> {
    const sessionRef = doc(
      this.firestore,
      `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}/${sessionId}`
    );

    await updateDoc(sessionRef, {
      endTime,
      lastUpdated: Date.now()
    });
  }

  async updatePostUpgradeSnapshot(deviceSerial: string, sessionId: string, postUpgradeSnapshot: DeviceSnapshot): Promise<void> {
    const sessionRef = doc(
      this.firestore,
      `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}/${sessionId}`
    );

    await updateDoc(sessionRef, {
      postUpgradeSnapshot,
      lastUpdated: Date.now()
    });
  }

  getDeviceUpgrades(deviceSerial: string, limitCount = 20): Observable<UpgradeSession[]> {
    const q = query(
      collection(this.firestore, `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}`),
      orderBy('startTime', 'desc'),
      limit(limitCount)
    );

    return collectionData(q, { idField: 'id' }) as Observable<UpgradeSession[]>;
  }

  getOperatorUpgrades(operatorId: string, dateRange?: DateRange): Observable<UpgradeSession[]> {
    let q = query(
      collectionGroup(this.firestore, LOGS_SUBCOLLECTION),
      where('operatorId', '==', operatorId),
      orderBy('startTime', 'desc')
    );

    if (dateRange) {
      q = query(
        collectionGroup(this.firestore, LOGS_SUBCOLLECTION),
        where('operatorId', '==', operatorId),
        where('startTime', '>=', dateRange.startDate),
        where('startTime', '<=', dateRange.endDate),
        orderBy('startTime', 'desc')
      );
    }

    return collectionData(q, { idField: 'id' }) as Observable<UpgradeSession[]>;
  }

  getErrorEvents(days = 7): Observable<UpgradeEvent[]> {
    const startDate = Date.now() - (days * 24 * 60 * 60 * 1000);
    const q = query(
      collectionGroup(this.firestore, EVENTS_SUBCOLLECTION),
      where('operation', '==', 'error'),
      where('timestamp', '>=', startDate),
      orderBy('timestamp', 'desc'),
      limit(100)
    );

    return collectionData(q, { idField: 'id' }) as Observable<UpgradeEvent[]>;
  }

  getSession(deviceSerial: string, sessionId: string): Observable<UpgradeSession | null> {
    const sessionRef = doc(
      this.firestore,
      `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}/${sessionId}`
    );

    return docData(sessionRef, { idField: 'id' }).pipe(
      map(session => session as UpgradeSession || null),
      catchError(error => {
        console.warn(`Session ${sessionId} not found for device ${deviceSerial}:`, error);
        return of(null);
      })
    );
  }

  getSessionEvents(deviceSerial: string, sessionId: string): Observable<UpgradeEvent[]> {
    const q = query(
      collection(
        this.firestore,
        `${SGP_UPGRADE_COLLECTION}/${deviceSerial}/${LOGS_SUBCOLLECTION}/${sessionId}/${EVENTS_SUBCOLLECTION}`
      ),
      orderBy('timestamp', 'asc')
    );

    return collectionData(q, { idField: 'id' }) as Observable<UpgradeEvent[]>;
  }

  async updateDeviceMetadata(deviceSerial: string, deviceModel: string): Promise<void> {
    const deviceRef = doc(this.firestore, `${SGP_UPGRADE_COLLECTION}/${deviceSerial}`);

    await setDoc(deviceRef, {
      deviceModel,
      lastUpgrade: Date.now(),
      totalUpgrades: increment(1)
    }, { merge: true });
  }
}
